import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * AreaDrawer — slide-out drawer for Add (single/bulk) / Edit area form.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   area: object | null,           // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (area: object) => void,
 *   areaTypes: Array<{ area_type_id: number, area_type_description: string }>,
 *   buildings: Array<{ building_id: number, building_name: string }>,
 *   areaGroups: Array<{ area_group_id: number, area_group_name: string }>
 * }} props
 */
const OCCUPANCY_EDITABLE_TYPES = new Set([1, 2, 3, 4]);

const AreaDrawer = ({ isOpen, onClose, area, onSave, onDelete, areaTypes, buildings, areaGroups, canModifyAreas = true, deleteBlockedReason = null }) => {
  const isEditMode = !!area;
  const drawerRef = useRef(null);
  // When the user lacks area-modify rights, the drawer collapses to area-group
  // assignment only — name/type/building/occupancy become read-only and the
  // Delete action is hidden.
  const fieldsLocked = isEditMode && !canModifyAreas;
  const fieldsLockedTitle = fieldsLocked ? 'You do not have permission to edit area details' : undefined;

  // Add mode toggle: 'single' | 'bulk'
  const [addMode, setAddMode] = useState('single');

  // Form state — single add / edit
  const [areaName, setAreaName] = useState('');
  const [maxOccupancy, setMaxOccupancy] = useState('');
  const [areaTypeId, setAreaTypeId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // Form state — bulk add
  const [baseName, setBaseName] = useState('');
  const [startNumber, setStartNumber] = useState('');
  const [endNumber, setEndNumber] = useState('');

  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Max occupancy is only editable for area types 1–4
  const isOccupancyEditable = OCCUPANCY_EDITABLE_TYPES.has(parseInt(areaTypeId, 10));

  // Lock max occupancy to 0 when the selected area type is not editable
  useEffect(() => {
    if (!isOccupancyEditable) {
      setMaxOccupancy('0');
    }
  }, [isOccupancyEditable]);

  // Populate form on open/area change
  useEffect(() => {
    if (isOpen && area) {
      setAreaName(area.area_name || '');
      setMaxOccupancy(area.max_occupancy != null ? String(area.max_occupancy) : '');
      setAreaTypeId(area.area_type_id?.toString() || '');
      setBuildingId(area.building_id?.toString() || '');
      setSelectedGroupIds(
        (area.area_groups || []).map((g) => g.area_group_id?.toString() || g.toString())
      );
      setAddMode('single');
      setShowDeleteConfirm(false);
    } else if (isOpen && !area) {
      setAreaName('');
      setMaxOccupancy('');
      setAreaTypeId('');
      setBuildingId('');
      setSelectedGroupIds([]);
      setBaseName('');
      setStartNumber('');
      setEndNumber('');
      setAddMode('single');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, area]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleGroupToggle = useCallback((groupId) => {
    const gStr = groupId.toString();
    setSelectedGroupIds((prev) =>
      prev.includes(gStr) ? prev.filter((id) => id !== gStr) : [...prev, gStr]
    );
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      try {
        if (!isEditMode && addMode === 'bulk') {
          const formData = {
            _bulk: true,
            baseName: baseName,
            startNumber: parseInt(startNumber, 10),
            endNumber: parseInt(endNumber, 10),
            max_occupancy: maxOccupancy ? parseInt(maxOccupancy, 10) : 0,
            area_type_id: parseInt(areaTypeId, 10),
            building_id: buildingId ? parseInt(buildingId, 10) : 0,
            area_group_ids: selectedGroupIds.map((id) => parseInt(id, 10)),
          };
          await onSave(formData);
          return;
        }

        const formData = {
          area_id: area?.area_id || null,
          area_name: areaName.trim(),
          max_occupancy: maxOccupancy ? parseInt(maxOccupancy, 10) : 0,
          area_type_id: parseInt(areaTypeId, 10),
          building_id: buildingId ? parseInt(buildingId, 10) : 0,
          area_group_ids: selectedGroupIds.map((id) => parseInt(id, 10)),
        };

        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [isEditMode, addMode, areaName, maxOccupancy, areaTypeId, buildingId, selectedGroupIds, baseName, startNumber, endNumber, area, onSave]
  );

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (area && onDelete) {
      onDelete(area);
    }
    setShowDeleteConfirm(false);
  }, [area, onDelete]);

  // Validation
  const isSingleValid = areaName.trim() && areaTypeId;
  const isBulkValid =
    baseName.trim() &&
    startNumber !== '' &&
    endNumber !== '' &&
    parseInt(endNumber, 10) >= parseInt(startNumber, 10) &&
    (parseInt(endNumber, 10) - parseInt(startNumber, 10)) <= 500 &&
    areaTypeId;
  const isFormValid = isEditMode ? isSingleValid : (addMode === 'single' ? isSingleValid : isBulkValid);

  const bulkCount =
    startNumber !== '' && endNumber !== '' && parseInt(endNumber, 10) >= parseInt(startNumber, 10)
      ? parseInt(endNumber, 10) - parseInt(startNumber, 10) + 1
      : 0;

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="ap-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`ap-drawer ap-drawer--lg ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit Area' : 'Add Area'}
      >
        {/* Header */}
        <div className="ap-drawer__header">
          <h2 className="ap-drawer__title">
            {isEditMode ? (fieldsLocked ? 'Edit Area Groups' : 'Edit Area') : 'Add Area'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ap-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="ap-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Single / Bulk tabs (Add mode only) */}
        {!isEditMode ? (
          <div className="ap-drawer-tabs">
            <button
              type="button"
              className={`ap-drawer-tab ${addMode === 'single' ? 'is-active' : ''}`}
              onClick={() => setAddMode('single')}
            >
              Single
            </button>
            <button
              type="button"
              className={`ap-drawer-tab ${addMode === 'bulk' ? 'is-active' : ''}`}
              onClick={() => setAddMode('bulk')}
            >
              Bulk
            </button>
          </div>
        ) : null}

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="ap-form">
          <div className="ap-form__body">
            {/* Single / Edit: Area Name */}
            {(isEditMode || addMode === 'single') ? (
              <div>
                <label htmlFor="drawer-area-name" className="ap-field__label">
                  Area Name <span className="ap-field__required">*</span>
                </label>
                <input
                  type="text"
                  id="drawer-area-name"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                  required
                  className="ap-field__input"
                  placeholder="Enter area name"
                  disabled={fieldsLocked}
                  title={fieldsLockedTitle}
                />
              </div>
            ) : (
              <>
                {/* Bulk: Base Name + Range */}
                <div>
                  <label htmlFor="drawer-bulk-name" className="ap-field__label">
                    Base Name <span className="ap-field__required">*</span>
                  </label>
                  <input
                    type="text"
                    id="drawer-bulk-name"
                    value={baseName}
                    onChange={(e) => setBaseName(e.target.value)}
                    required
                    className="ap-field__input"
                    placeholder="e.g. Room"
                  />
                </div>
                <div className="ap-form-grid">
                  <div>
                    <label htmlFor="drawer-bulk-start" className="ap-field__label">
                      Start Number <span className="ap-field__required">*</span>
                    </label>
                    <input
                      type="number"
                      id="drawer-bulk-start"
                      value={startNumber}
                      onChange={(e) => setStartNumber(e.target.value)}
                      required
                      min="0"
                      className="ap-field__input"
                      placeholder="e.g. 100"
                    />
                  </div>
                  <div>
                    <label htmlFor="drawer-bulk-end" className="ap-field__label">
                      End Number <span className="ap-field__required">*</span>
                    </label>
                    <input
                      type="number"
                      id="drawer-bulk-end"
                      value={endNumber}
                      onChange={(e) => setEndNumber(e.target.value)}
                      required
                      min="0"
                      className="ap-field__input"
                      placeholder="e.g. 120"
                    />
                  </div>
                </div>
                {bulkCount > 0 ? (
                  <p className="ap-field__hint">
                    Will create {bulkCount} area{bulkCount !== 1 ? 's' : ''}: {baseName}{startNumber} &ndash; {baseName}{endNumber}
                    {bulkCount > 500 ? (
                      <span className="ap-field__error"> (max 500 per batch)</span>
                    ) : null}
                  </p>
                ) : null}
              </>
            )}

            {/* Area Type */}
            <div>
              <label htmlFor="drawer-area-type" className="ap-field__label">
                Area Type <span className="ap-field__required">*</span>
              </label>
              <select
                id="drawer-area-type"
                value={areaTypeId}
                onChange={(e) => setAreaTypeId(e.target.value)}
                required
                className="ap-field__select"
                disabled={fieldsLocked}
                title={fieldsLockedTitle}
              >
                <option value="">Select area type...</option>
                {(areaTypes || []).map((t) => (
                  <option key={t.area_type_id} value={t.area_type_id}>
                    {t.area_type_description}
                  </option>
                ))}
              </select>
            </div>

            {/* Building */}
            <div>
              <label htmlFor="drawer-building" className="ap-field__label">Building</label>
              <select
                id="drawer-building"
                value={buildingId}
                onChange={(e) => setBuildingId(e.target.value)}
                className="ap-field__select"
                disabled={fieldsLocked}
                title={fieldsLockedTitle}
              >
                <option value="">No building</option>
                {(buildings || []).map((b) => (
                  <option key={b.building_id} value={b.building_id}>
                    {b.building_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Max Occupancy — editable only for area types 1–4 */}
            <div>
              <label htmlFor="drawer-max-occupancy" className="ap-field__label">Max Occupancy</label>
              <input
                type="number"
                id="drawer-max-occupancy"
                value={maxOccupancy}
                onChange={(e) => setMaxOccupancy(e.target.value)}
                min="0"
                className="ap-field__input"
                placeholder="e.g. 2"
                disabled={!isOccupancyEditable || fieldsLocked}
                title={fieldsLockedTitle}
              />
            </div>

            {/* Area Groups */}
            <div>
              <p className="ap-field__label">Area Groups</p>
              {(areaGroups || []).length === 0 ? (
                <p className="ap-field__hint">No area groups available</p>
              ) : (
                <div className="ap-group-grid">
                  {(areaGroups || []).map((group) => {
                    const isChecked = selectedGroupIds.includes(group.area_group_id.toString());
                    return (
                      <label
                        key={group.area_group_id}
                        className={`ap-group-option ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleGroupToggle(group.area_group_id)}
                          className="ap-checkbox"
                        />
                        <span className="ap-group-option__text">{group.area_group_name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Footer with actions */}
          <div className="ap-form__footer">
            {/* Delete button (edit mode only, hidden when role lacks rights) */}
            {isEditMode && canModifyAreas && !showDeleteConfirm ? (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="ap-btn ap-btn--danger"
                disabled={!!deleteBlockedReason}
                title={deleteBlockedReason || undefined}
              >
                <i className="fe fe-trash-2" aria-hidden="true" />
                Delete
              </button>
            ) : null}

            {/* Delete confirmation */}
            {isEditMode && showDeleteConfirm ? (
              <div className="ap-delete-confirm">
                <span className="ap-delete-confirm__text">
                  Delete <strong>{area?.area_name}</strong>?
                </span>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="ap-btn ap-btn--cancel ap-btn--sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="ap-btn ap-btn--danger-solid ap-btn--sm"
                >
                  Delete
                </button>
              </div>
            ) : null}

            {/* Spacer pushes Cancel/Save right */}
            {!showDeleteConfirm ? <div style={{ flex: 1 }} /> : null}

            {!showDeleteConfirm ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="ap-btn ap-btn--cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || saving}
                  className="ap-btn ap-btn--primary"
                >
                  {saving ? (
                    <span className="ap-saving-text">
                      <svg className="ap-icon-md ap-spinner" fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    isEditMode ? 'Save Changes' : (addMode === 'bulk' ? `Add ${bulkCount} Areas` : 'Add Area')
                  )}
                </button>
              </>
            ) : null}
          </div>
        </form>
      </div>
    </>
  );
};

export default AreaDrawer;
